import { useState, useRef, useMemo, useCallback } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { parseCSVRows } from "@/lib/csv"
import { BILLING_STATUS_LABELS, BILLING_STATUS_COLORS, ALL_BILLING_STATUSES, type BillingStatus } from "@/lib/billing-status"
import { Trash2Icon, AlertCircleIcon, CheckCircle2Icon, DownloadIcon, FileSpreadsheetIcon } from "lucide-react"

interface ParsedRow {
  internalId: number
  billingNotes: string | null
  billingTags: string[] | null
}

interface PreviewRow extends ParsedRow {
  found: boolean
  billingStatus: BillingStatus | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BillingImportDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)

  const [parseError, setParseError] = useState<string | null>(null)
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([])
  const [isPreviewing, setIsPreviewing] = useState(false)

  // Bulk overrides
  const [bulkEnabled, setBulkEnabled] = useState(false)
  const [bulkBillingStatus, setBulkBillingStatus] = useState<BillingStatus | "">("")
  const [bulkBillingNotes, setBulkBillingNotes] = useState("")
  const [bulkBillingTags, setBulkBillingTags] = useState("")

  const [formError, setFormError] = useState<string | null>(null)
  const [previewPage, setPreviewPage] = useState(1)

  const PREVIEW_PAGE_SIZE = 50

  const hasErrors  = previewRows.some(r => !r.found)
  const foundRows  = previewRows.filter(r => r.found)
  const notFoundRows = previewRows.filter(r => !r.found)

  // Errors always first, then found rows ordered by internalId
  const sortedRows = useMemo(() => [
    ...notFoundRows,
    ...foundRows,
  ], [previewRows]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalPreviewPages = Math.max(1, Math.ceil(sortedRows.length / PREVIEW_PAGE_SIZE))
  const pagedRows = sortedRows.slice((previewPage - 1) * PREVIEW_PAGE_SIZE, previewPage * PREVIEW_PAGE_SIZE)

  function resetState() {
    setParseError(null)
    setPreviewRows([])
    setPreviewPage(1)
    setIsPreviewing(false)
    setBulkEnabled(false)
    setBulkBillingStatus("")
    setBulkBillingNotes("")
    setBulkBillingTags("")
    setFormError(null)
    if (fileRef.current) fileRef.current.value = ""
  }

  function handleClose(v: boolean) {
    if (!v) resetState()
    onOpenChange(v)
  }


  function rowsFromMatrix(matrix: unknown[][]): ParsedRow[] | string {
    const rows: ParsedRow[] = []
    for (const cols of matrix) {
      const raw = String(cols[0] ?? "").trim()
      if (!raw) continue
      const normalized = /^SH\d+$/i.test(raw) ? raw.slice(2) : raw
      const num = Number(normalized)
      // Skip non-numeric rows silently — handles header rows automatically
      if (!Number.isInteger(num) || num <= 0) continue
      const notesRaw = String(cols[1] ?? "").trim() || null
      const tagsRaw  = String(cols[2] ?? "").trim() || null
      const tags = tagsRaw ? tagsRaw.split("|").map(t => t.trim()).filter(Boolean) : null
      rows.push({ internalId: num, billingNotes: notesRaw, billingTags: tags?.length ? tags : null })
    }
    if (rows.length === 0) return "El archivo no contiene filas con IDs válidos"
    if (rows.length > 1000) return `El archivo contiene ${rows.length} filas, pero el límite es 1000. Dividí el archivo en partes más pequeñas e importalas por separado.`
    return rows
  }

  function parseCsvText(text: string): string[][] {
    const firstLine = text.split(/\r?\n/, 1)[0] ?? ""
    const delimiter = firstLine.split("\t").length > firstLine.split(",").length ? "\t" : ","
    if (delimiter === "\t") {
      return text.split(/\r?\n/).filter(line => line.trim()).map(line => line.split("\t").map(cell => cell.trim()))
    }
    return parseCSVRows(text).filter(row => row.some(cell => cell.trim()))
  }

  const parseFile = useCallback(async (file: File): Promise<ParsedRow[] | string> => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? ""
    if (ext === "xlsx" || ext === "xls") {
      const XLSX = await import("xlsx")
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: "array" })
      const sheetName = wb.SheetNames[0]
      if (!sheetName) return "El archivo no contiene hojas válidas"
      const ws = wb.Sheets[sheetName]!
      const matrix = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: "" }) as string[][]
      return rowsFromMatrix(matrix)
    }
    // CSV / TXT
    const text = await file.text()
    return rowsFromMatrix(parseCsvText(text))
  }, [])

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setParseError(null)
    setPreviewRows([])
    setFormError(null)

    const result = await parseFile(file)
    if (typeof result === "string") {
      setParseError(result)
      if (fileRef.current) fileRef.current.value = ""
      return
    }

    setIsPreviewing(true)
    try {
      const res = await authorizedFetch(`${API_URL}/api/shipments/billing-import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: result, preview: true }),
      })
      const json = await res.json() as { found: { internalId: number; billingStatus: BillingStatus | null }[]; notFound: number[] }
      const foundMap = new Map(json.found.map(f => [f.internalId, f.billingStatus]))
      const merged: PreviewRow[] = result.map(r => ({
        ...r,
        found: foundMap.has(r.internalId),
        billingStatus: foundMap.get(r.internalId) ?? null,
      }))
      setPreviewRows(merged)
      setPreviewPage(1)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Error al previsualizar")
    } finally {
      setIsPreviewing(false)
    }
  }

  function removeRow(internalId: number) {
    setPreviewRows(prev => {
      const next = prev.filter(r => r.internalId !== internalId)
      // If removing the last item on the current page, go back one page
      const nextTotal = Math.max(1, Math.ceil(next.length / PREVIEW_PAGE_SIZE))
      setPreviewPage(p => Math.min(p, nextTotal))
      return next
    })
  }

  const confirmMut = useMutation({
    mutationFn: async () => {
      const rows = foundRows.map(r => ({
        internalId: r.internalId,
        billingNotes: r.billingNotes,
        billingTags: r.billingTags,
      }))
      const body: Record<string, unknown> = { rows }
      if (bulkEnabled) {
        if (bulkBillingStatus) body.bulkBillingStatus = bulkBillingStatus === "pendiente" ? null : bulkBillingStatus
        if (bulkBillingNotes.trim()) body.bulkBillingNotes = bulkBillingNotes.trim()
        if (bulkBillingTags.trim()) body.bulkBillingTags = bulkBillingTags.trim().split("|").map(t => t.trim()).filter(Boolean)
      }
      const res = await authorizedFetch(`${API_URL}/api/shipments/billing-import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      return res.json()
    },
    onSuccess: async (_data, _vars, _ctx) => {
      const count = foundRows.length
      handleClose(false)
      await qc.refetchQueries({ queryKey: ["shipments-billing"], type: "active" })
      toast.success(`${count} carga${count !== 1 ? "s" : ""} actualizadas`)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const canConfirm = foundRows.length > 0 && !hasErrors && !confirmMut.isPending

  async function downloadExample() {
    const XLSX = await import("xlsx")
    const data = [
      ["ID", "Observaciones", "Tags (separados por |)"],
      [123, "Factura pendiente de revisión", "urgente|prioridad"],
      [456, "", "cliente-vip"],
      [789, "Sin observaciones", ""],
      [1042, "", ""],
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    // Column widths
    ws["!cols"] = [{ wch: 10 }, { wch: 35 }, { wch: 25 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Importación")
    XLSX.writeFile(wb, "ejemplo-importacion-facturacion.xlsx")
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>Importar facturación</DialogTitle>
          <DialogDescription className="sr-only">Importar facturación desde CSV</DialogDescription>
          <div className="mt-2 space-y-2 text-sm text-muted-foreground">
              <ol className="list-decimal list-inside space-y-1">
                <li>Preparar un Excel o CSV con columnas: <code className="text-xs bg-muted px-1 rounded font-mono">ID, Observaciones (opt), Tags separados por | (opt)</code></li>
                <li>Subir el archivo — el sistema busca cada ID y muestra cuáles no existen.</li>
                <li>Eliminar las filas en rojo antes de continuar.</li>
                <li>Opcionalmente, activar <strong className="text-foreground font-medium">Actualización masiva</strong> para aplicar el mismo estado, observaciones o tags a todas las cargas del archivo.</li>
                <li>Confirmar — los cambios se aplican y la tabla se actualiza.</li>
              </ol>
              <p className="text-xs">Formatos aceptados: <strong>.xlsx, .xls, .csv</strong>. Máximo 1000 filas por importación. Las filas con encabezados de texto se ignoran automáticamente.</p>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* File input */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="billing-csv">Archivo</Label>
              <button
                type="button"
                onClick={downloadExample}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <FileSpreadsheetIcon className="size-3" />
                Descargar ejemplo .xlsx
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="billing-csv"
                ref={fileRef}
                type="file"
                accept=".csv,.xls,.xlsx,text/csv"
                onChange={handleFileChange}
                disabled={isPreviewing}
                className="text-sm"
              />
              {isPreviewing && (
                <span className="text-xs text-muted-foreground shrink-0">Procesando...</span>
              )}
            </div>
            {parseError && (
              <p className="text-sm text-destructive flex items-center gap-1.5">
                <AlertCircleIcon className="size-3.5 shrink-0" />
                {parseError}
              </p>
            )}
          </div>

          {/* Preview table */}
          {previewRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">
                    {foundRows.length} encontrada{foundRows.length !== 1 ? "s" : ""}
                  </span>
                  {notFoundRows.length > 0 && (
                    <span className="text-sm text-destructive font-medium">
                      · {notFoundRows.length} no encontrada{notFoundRows.length !== 1 ? "s" : ""} — eliminá las filas en rojo para continuar
                    </span>
                  )}
                </div>
                {totalPreviewPages > 1 && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                      disabled={previewPage === 1}
                      className="size-6 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30 transition-colors"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {previewPage} / {totalPreviewPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPreviewPage(p => Math.min(totalPreviewPages, p + 1))}
                      disabled={previewPage === totalPreviewPages}
                      className="size-6 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30 transition-colors"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground w-20">#ID</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground w-28">Estado actual</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Observaciones</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Tags</th>
                      <th className="w-8 px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pagedRows.map(row => (
                      <tr
                        key={row.internalId}
                        className={cn(
                          row.found ? "bg-background" : "bg-red-50 dark:bg-red-950/20",
                        )}
                      >
                        <td className="px-3 py-2 font-mono font-medium">
                          <span className="flex items-center gap-1">
                            {row.found
                              ? <CheckCircle2Icon className="size-3 text-emerald-500 shrink-0" />
                              : <AlertCircleIcon className="size-3 text-destructive shrink-0" />
                            }
                            #{row.internalId}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {row.found ? (
                            <span className={cn(
                              "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                              BILLING_STATUS_COLORS[row.billingStatus ?? "pendiente"],
                            )}>
                              {BILLING_STATUS_LABELS[row.billingStatus ?? "pendiente"]}
                            </span>
                          ) : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground max-w-50 truncate">
                          {row.billingNotes ?? <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {row.billingTags?.length ? (
                            <div className="flex flex-wrap gap-1">
                              {row.billingTags.map(t => (
                                <span key={t} className="inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                                  {t}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            onClick={() => removeRow(row.internalId)}
                            className="text-muted-foreground/40 hover:text-destructive transition-colors"
                            title="Eliminar fila"
                          >
                            <Trash2Icon className="size-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bulk override section */}
          {previewRows.length > 0 && (
            <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={bulkEnabled}
                  onChange={e => setBulkEnabled(e.target.checked)}
                  className="size-4 rounded border-border"
                />
                <span className="text-sm font-medium">
                  Actualización masiva — aplicar a todas las cargas encontradas
                </span>
              </label>

              {bulkEnabled && (
                <div className="grid grid-cols-1 gap-3 pt-1 pl-6">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Estado de facturación (opcional)</Label>
                    <Select
                      value={bulkBillingStatus || "__none__"}
                      onValueChange={v => setBulkBillingStatus(v === "__none__" ? "" : v as BillingStatus)}
                    >
                      <SelectTrigger className="h-8 text-xs w-48">
                        <SelectValue placeholder="Sin cambio de estado">
                          {bulkBillingStatus ? BILLING_STATUS_LABELS[bulkBillingStatus] : "Sin cambio de estado"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" className="text-xs text-muted-foreground">Sin cambio de estado</SelectItem>
                        {ALL_BILLING_STATUSES.map(s => (
                          <SelectItem key={s} value={s} className="text-xs">
                            {BILLING_STATUS_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Si no seleccionás, se conserva el estado actual de cada carga.</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Observaciones masivas (opcional)</Label>
                    <Input
                      value={bulkBillingNotes}
                      onChange={e => setBulkBillingNotes(e.target.value)}
                      placeholder="Sobreescribe observaciones del CSV..."
                      className="h-8 text-xs"
                    />
                    <p className="text-[10px] text-muted-foreground">Si completás este campo, sobreescribe las observaciones individuales del CSV.</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Tags masivos (opcional, separados por |)</Label>
                    <Input
                      value={bulkBillingTags}
                      onChange={e => setBulkBillingTags(e.target.value)}
                      placeholder="tag1|tag2|tag3"
                      className="h-8 text-xs"
                    />
                    <p className="text-[10px] text-muted-foreground">Si completás este campo, sobreescribe los tags individuales del CSV.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {formError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={() => handleClose(false)} disabled={confirmMut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => confirmMut.mutate()}
            disabled={!canConfirm}
          >
            {confirmMut.isPending ? "Actualizando..." : `Confirmar ${foundRows.length > 0 ? `(${foundRows.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
